import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '../context/ChatContext';
import { mediaUrl, REACTION_EMOJIS, chatUsers } from '../api/chat';
import { onAttachmentClick } from '../download';
import { listCustMessages, sendCustReply, custMute, custReact, custRecallMessage, custRetryMessage, custPinConversation, createCustConversation, getMyChannels, uploadCustomerChatFiles, getStarConfig, setStarConfig, createCustTask, acceptCustTask, completeCustTask, deleteCustTask } from '../api/customerChat';
import ChatTaskModal from './ChatTaskModal';
import ChatTasksPanel, { openTaskCount } from './ChatTasksPanel';
import { searchCustomers } from '../api/customers';
import { fmtSize } from '../utils/chatFiles';
import ImageLightbox from './ImageLightbox';
import { linkifyText } from '../utils/linkify';
import { getSocket, emitCustView, emitCustTyping } from '../socket';
import { SVC_ORDER, SVC_SHORT, SVC_LABEL, groupByCustomer, channelsOfSameCustomer } from '../utils/custChannels';
import { useToast } from './Toast';
import EmojiPicker from './EmojiPicker';

// ===== Chat KHÁCH HÀNG (tab "Khách hàng") =====
// Giao diện + thao tác Y HỆT chat nội bộ (dùng chung class CSS chat*), chỉ khác nguồn dữ liệu:
// KH nhắn từ web/app bidnhanh đổ vào qua webhook; NV trả lời sẽ đẩy ngược sang bidnhanh.
// Các helper nhỏ dưới đây LẶP LẠI từ ChatWidget.jsx (không import chéo để tránh vòng import).

const EMOJI_ONLY = /^(?:[\p{Extended_Pictographic}‍️⃣\u{1F3FB}-\u{1F3FF}]|\s)+$/u;
function isBigEmoji(t) {
  if (!t) return false;
  const s = t.trim();
  if (!s || !EMOJI_ONLY.test(s)) return false;
  return [...s].filter((c) => !/\s/.test(c)).length <= 6;
}
const initials = (name) => (name || '?').trim().split(/\s+/).slice(-1)[0]?.[0]?.toUpperCase() || '?';
const fmtTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (dt.toDateString() === new Date().toDateString()) return dt.toLocaleTimeString('vi', { hour: '2-digit', minute: '2-digit' });
  const p = (n) => String(n).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()}`;
};
const fmtMsgTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const now = new Date();
  const hm = dt.toLocaleTimeString('vi', { hour: '2-digit', minute: '2-digit' });
  if (dt.toDateString() === now.toDateString()) return hm;
  const p = (n) => String(n).padStart(2, '0');
  const dm = `${p(dt.getDate())}/${p(dt.getMonth() + 1)}`;
  return dt.getFullYear() === now.getFullYear() ? `${hm} · ${dm}` : `${hm} · ${dm}/${dt.getFullYear()}`;
};
// Vạch ngày (Hôm nay / Hôm qua / Thứ …, DD/MM/YYYY) — giống chat nội bộ.
const fmtDayLabel = (d) => {
  const dt = new Date(d);
  const now = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOf(now) - startOf(dt)) / dayMs);
  if (diff === 0) return 'Hôm nay';
  if (diff === 1) return 'Hôm qua';
  const p = (n) => String(n).padStart(2, '0');
  const base = `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()}`;
  if (diff > 1 && diff < 7) {
    const wd = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'][dt.getDay()];
    return `${wd}, ${base}`;
  }
  return base;
};
// Ảnh = type image/* HOẶC đuôi file là ảnh (nguồn bidnhanh hay thiếu type -> đừng coi ảnh là tệp).
const isImageAtt = (a) => (a.type || '').startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp|heic|heif|svg|avif)(\?|#|$)/i.test(a.name || a.url || a.data || '');
function AttachmentView({ a }) {
  const src = mediaUrl(a.url || a.data);
  // Bong bóng chat hiện BẢN THU NHỎ (media server dựng, cạnh dài ≤480px) thay vì ảnh gốc:
  // ảnh chụp điện thoại thường 3-5 MB, bản thu nhỏ ~30-60 KB. Mở 30 tin có ảnh là chênh nhau
  // cả trăm MB tải về — đáng kể trên 4G.
  // Tệp CŨ (trước khi có media server) không có thumbUrl -> vẫn dùng ảnh gốc như trước.
  // data-full giữ url ảnh GỐC để lightbox phóng to lấy đúng bản nét (xem onClick ở .chatmsgs).
  const thumb = a.thumbUrl ? mediaUrl(a.thumbUrl) : '';
  if (isImageAtt(a)) {
    return (
      <img className="chat-att-img" src={thumb || src} data-full={src} alt={a.name} title="Bấm để xem to" style={{ cursor: 'zoom-in' }} loading="lazy" decoding="async"
        onError={(e) => {
          const el = e.currentTarget;
          // Bản thu nhỏ hỏng (media server bận, ảnh lỗi không dựng nổi) -> lùi về ảnh gốc ĐÚNG
          // MỘT lần rồi mới báo vỡ. Cờ data-retry chặn vòng lặp onError vô tận khi cả hai đều hỏng.
          if (thumb && el.dataset.retry !== '1') { el.dataset.retry = '1'; el.src = src; return; }
          el.classList.add('chat-att-img--broken'); el.removeAttribute('title'); el.alt = 'Ảnh không tải được';
        }} />
    );
  }
  return (
    <a className="chat-att-file" href={src} download={a.name} target="_blank" rel="noreferrer" onClick={onAttachmentClick(src, a.name)}>
      📎 <span className="chat-att-name">{a.name}</span>
      <span className="chat-att-size">{fmtSize(a.size)}</span>
    </a>
  );
}

// Ai được thấy hội thoại KH "chưa gán" (đồng bộ backend accessScope): GĐ/CV + Phòng CSKH/DVKH.
export const canSeeAllKh = (me) =>
  me?.role === 'GD' || me?.role === 'CV' || (me?.departments || []).some((d) => ['Phòng CSKH', 'Phòng DVKH'].includes(d));

// Ai được thấy TÊN SALE care khách + có nút LỌC theo nhân viên: canSeeAllKh (GĐ/CV + CSKH/DVKH) + TP + Phó phòng.
export const canSeeSaleKh = (me) => canSeeAllKh(me) || me?.role === 'TP' || !!me?.isDeputy;

// Ai được TẮT ÂM thông báo KH (toàn bộ + từng khách): CHỈ Trưởng phòng & Giám đốc.
// Nhân viên KHÔNG có nút tắt thông báo (không cho tự tắt để khỏi bỏ lỡ tin khách).
export const canMuteKh = (me) => me?.role === 'GD' || me?.role === 'TP';

// Danh sách hội thoại KH — cùng kiểu dòng với danh sách nội bộ.
// Panel "Nhắn khách hàng mới" trong CỬA SỔ CHAT NỔI: tìm KH -> tạo/mở hội thoại -> vào chat luôn.
// Có cần hiện ô chọn kênh/mã khi CHỦ ĐỘNG nhắn KH không?
//   - NV có >1 kênh -> chọn kênh (A).
//   - KH có mã phụ VÀ NV nhắn được kênh 'kd' -> NV kinh doanh chọn mã chat (B). Chọn mã chỉ có ý nghĩa
//     ở kênh kd (mã đó thành mã chính); phòng khác luôn dùng mã chính -> không cần hỏi.
export const needsChatChoice = (opt, channels) =>
  (channels || []).length > 1 || (((opt.aliasCodes || []).length > 0) && (channels || []).includes('kd'));

// Ô CHỌN kênh + mã trước khi mở hội thoại. onStart(code, svcType). channels = kênh NV được nhắn.
export function StartCustChat({ opt, channels, busy, onStart, onCancel }) {
  const codes = [opt.code, ...(opt.aliasCodes || [])].filter((c, i, a) => c && a.indexOf(c) === i);
  const [svc, setSvc] = useState((channels || [])[0] || 'kd');
  const [code, setCode] = useState(opt.chatCode && codes.includes(opt.chatCode) ? opt.chatCode : opt.code);
  const multiChan = (channels || []).length > 1;
  const showCode = svc === 'kd' && codes.length > 1; // chọn mã chỉ ở kênh kd
  const sel = { width: '100%', boxSizing: 'border-box', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' };
  return (
    <div style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8, margin: '6px 0', background: 'var(--card, var(--bg))' }}>
      <div className="chatuser-name" style={{ marginBottom: 8 }}>{opt.name || opt.code}</div>
      {multiChan && (
        <div style={{ marginBottom: 8 }}>
          <div className="small muted" style={{ marginBottom: 4 }}>Nhắn bằng kênh</div>
          <select value={svc} onChange={(e) => setSvc(e.target.value)} style={sel}>
            {channels.map((c) => <option key={c} value={c}>{SVC_LABEL[c] || c}</option>)}
          </select>
        </div>
      )}
      {showCode && (
        <div style={{ marginBottom: 8 }}>
          <div className="small muted" style={{ marginBottom: 4 }}>Nhắn bằng mã KH</div>
          <select value={code} onChange={(e) => setCode(e.target.value)} style={sel}>
            {codes.map((c) => <option key={c} value={c}>{c}{c === opt.chatCode ? ' · mã chính' : c === opt.code ? ' · mã gốc' : ''}</option>)}
          </select>
          <div className="small muted" style={{ marginTop: 4 }}>Mã bạn chọn sẽ thành mã chính, gợi ý cho các phòng ban khác.</div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
        <button type="button" onClick={onCancel} disabled={busy} style={{ padding: '6px 12px' }}>Huỷ</button>
        <button type="button" onClick={() => onStart(showCode ? code : (opt.chatCode || opt.code), svc, opt.source)} disabled={busy}
          style={{ padding: '6px 12px', background: 'var(--primary, #4f46e5)', color: '#fff', border: 'none', borderRadius: 6, cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? 'Đang mở…' : 'Bắt đầu chat'}
        </button>
      </div>
    </div>
  );
}

export function NewCustomerChat({ onDone }) {
  const { openConv, refreshCust } = useChat();
  const [q, setQ] = useState('');
  const [opts, setOpts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [channels, setChannels] = useState([]);
  const [sel, setSel] = useState(null); // KH đang chọn kênh/mã (cần hỏi thêm)
  useEffect(() => { getMyChannels().then(setChannels).catch(() => setChannels([])); }, []);
  useEffect(() => {
    const t = q.trim();
    if (!t) { setOpts([]); return; }
    const id = setTimeout(() => searchCustomers(t, true, true).then(setOpts).catch(() => {}), 250); // forChat: CSKH/DVKH tìm được mọi KH
    return () => clearTimeout(id);
  }, [q]);
  function pick(o) {
    if (busy) return;
    if (needsChatChoice(o, channels)) { setSel(o); setErr(''); }
    else startChat(o.code, channels[0], o.source);
  }
  async function startChat(code, svc, source) {
    if (busy) return;
    setBusy(true); setErr('');
    try {
      const conv = await createCustConversation(code, svc, source);
      refreshCust();
      openConv({ ...conv, id: conv._id, kind: 'customer', name: conv.custName || conv.custCode || 'Khách hàng' });
      onDone && onDone();
    } catch (e) {
      setErr(e.response?.data?.message || 'Không tạo được hội thoại');
    } finally { setBusy(false); }
  }
  return (
    <div className="chatuserlist" style={{ padding: '8px 10px' }}>
      <input autoFocus style={{ width: '100%', boxSizing: 'border-box' }} value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="🔍 Tìm mã / tên khách hàng…" />
      {err && <div className="small" style={{ color: 'var(--danger)', marginTop: 8 }}>{err}</div>}
      {sel && <StartCustChat opt={sel} channels={channels} busy={busy} onStart={startChat} onCancel={() => setSel(null)} />}
      {!q.trim() && !sel && <div className="chatempty">Gõ mã hoặc tên KH để chủ động nhắn tin.</div>}
      {q.trim() && !opts.length && <div className="chatempty">Không tìm thấy khách hàng.</div>}
      {opts.slice(0, 30).map((o) => (
        <div key={o.id || o.code} className="chatuser-row" style={{ cursor: busy ? 'wait' : 'pointer' }} onClick={() => pick(o)}>
          <span className="chat-avatar sm cust">{initials(o.name || o.code)}</span>
          <div className="chatuser-main">
            <div className="chatuser-name">{o.name || o.code}</div>
            <div className="chatuser-sub"><code>{o.code}</code>{o.sale ? ` · sale: ${o.sale}` : ''}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Chip các KÊNH của 1 khách trong dòng danh sách (KD/CSKH/Mua + số chưa đọc) + tag NGUỒN (SocSon/PMBox).
function ChannelChips({ group }) {
  const src = group.rep?.channel === 'jtool' ? 'jtool' : 'socson';
  return (
    <div className="cust-chips">
      <span className={'cust-src cust-src-' + src} title={src === 'jtool' ? 'Khách từ PMBox (jtool)' : 'Khách từ SocSon'}>
        {src === 'jtool' ? 'PMBox' : 'SocSon'}
      </span>
      {SVC_ORDER.filter((t) => group.chans[t]).map((t) => {
        const n = group.chans[t].unread || 0;
        return (
          <span key={t} className={'cust-chip' + (n ? ' has' : '')}>
            {SVC_SHORT[t]}{n > 0 && <b>{n > 9 ? '9+' : n}</b>}
          </span>
        );
      })}
    </div>
  );
}

// Popup GIÁM ĐỐC đặt ngưỡng đánh dấu sao KH (trong màn quản lý chat). onClose(saved?) — saved=true để cha làm mới.
function StarThresholdModal({ onClose }) {
  const [mh, setMh] = useState('');
  const [vc, setVc] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const fmt = (n) => (Number(n) || 0).toLocaleString('vi-VN');
  const parse = (s) => Number(String(s).replace(/[^\d]/g, '')) || 0;
  useEffect(() => {
    getStarConfig()
      .then((c) => { setMh(fmt(c.mhThreshold)); setVc(fmt(c.vcThreshold)); })
      .catch(() => setErr('Không tải được cấu hình'))
      .finally(() => setLoading(false));
  }, []);
  const save = async () => {
    setSaving(true); setErr('');
    try { await setStarConfig(parse(mh), parse(vc)); onClose(true); }
    catch (e) { setErr(e?.response?.data?.message || 'Lưu thất bại'); setSaving(false); }
  };
  return (
    <div className="cust-star-backdrop" onClick={() => onClose()}>
      <div className="cust-star-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cust-star-head">
          <span>⭐ Ngưỡng đánh dấu sao khách hàng</span>
          <button type="button" className="cust-star-x" onClick={() => onClose()} aria-label="Đóng">✕</button>
        </div>
        <div className="cust-star-body">
          <div className="small muted" style={{ marginBottom: 12 }}>
            KH có doanh thu trung bình/tháng (theo bảng lũy kế năm nay) vượt <b>một trong hai</b> ngưỡng sẽ được gắn ⭐.
          </div>
          {loading ? <div className="chatempty">Đang tải…</div> : (
            <>
              <label className="cust-star-label">Doanh thu mua hộ lớn hơn (đ)</label>
              <input className="cust-star-input" value={mh} inputMode="numeric" onChange={(e) => setMh(fmt(parse(e.target.value)))} />
              <label className="cust-star-label">Doanh thu vận chuyển lớn hơn (đ)</label>
              <input className="cust-star-input" value={vc} inputMode="numeric" onChange={(e) => setVc(fmt(parse(e.target.value)))} />
              {err && <div className="small" style={{ color: 'var(--danger)', marginTop: 8 }}>{err}</div>}
              <button type="button" className="cust-star-save" onClick={save} disabled={saving}>{saving ? 'Đang lưu…' : 'Lưu ngưỡng'}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function CustomerConversationList({ q = '' }) {
  const { custConvs, openConv, windows, me, patchCustConv, drafts, refreshCust } = useChat();
  const admin = canSeeAllKh(me);
  const canSeeSale = canSeeSaleKh(me); // GĐ/CV/CSKH/DVKH + TP + PP: thấy tên sale + lọc theo NV
  const myId = String(me?.id || me?._id || ''); // để ẩn tên sale khi KH do CHÍNH MÌNH care
  const isGD = me?.role === 'GD' || me?.role === 'CV'; // chỉ GĐ/CV đặt ngưỡng đánh dấu sao
  const [filterSaleUser, setFilterSaleUser] = useState(''); // '' = mọi NV; id sale -> chỉ KH của sale đó
  const [unreadOnly, setUnreadOnly] = useState(false); // chỉ hiện KH có tin CHƯA ĐỌC
  const [showStar, setShowStar] = useState(false); // popup GĐ đặt ngưỡng đánh dấu sao
  // (Tắt/bật thông báo hệ thống theo TỪNG LUỒNG đã chuyển sang panel "Cài đặt thông báo" — gọn danh sách.)
  const fold = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();
  const kw = fold(q).trim();
  // Danh sách NV để LỌC = các sale khác nhau trong những hội thoại đang thấy (theo phạm vi của mình).
  const saleOptions = [...custConvs.reduce((m, c) => (c.custSaleUser && c.custSaleName ? m.set(String(c.custSaleUser), c.custSaleName) : m), new Map()).entries()]
    .map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'vi'));
  let listShown = kw ? custConvs.filter((c) => fold(c.name).includes(kw) || fold(c.custCode).includes(kw)) : custConvs;
  if (canSeeSale && filterSaleUser) listShown = listShown.filter((c) => String(c.custSaleUser) === filterSaleUser); // lọc theo NV
  // GOM THEO KHÁCH: mỗi khách 1 dòng, các kênh thành chip. Mở dòng = mở kênh đại diện (có tin mới).
  let groups = groupByCustomer(listShown);
  if (unreadOnly) groups = groups.filter((g) => (g.unread || 0) > 0); // chỉ KH có tin CHƯA ĐỌC
  // MỞ ĐÚNG HÀNG ĐÃ NHẤN: danh sách tự ĐẨY khách có tin mới lên đầu -> nếu mở bằng onClick, tin đến ngay
  // lúc chạm sẽ đảo chỗ hàng, ngón tay rơi trúng khách khác -> "bấm khách này lại mở khách khác". Khắc phục:
  // ghi lại hội thoại tại lúc BẤM XUỐNG (trước khi kịp đảo), mở khi THẢ nếu là chạm (không phải cuộn).
  const pressRef = useRef(null);
  const onListPointerUp = (e) => {
    const p = pressRef.current; pressRef.current = null;
    if (!p) return;
    const moved = Math.hypot((e.clientX || 0) - p.x, (e.clientY || 0) - p.y);
    if (moved <= 12 && Date.now() - p.t <= 700) openConv(p.rep); // chạm (không lê > 12px, < 700ms) mới mở
  };
  const togglePinConv = async (e, c) => {
    e.stopPropagation();
    try {
      const r = await custPinConversation(c.id, !c.pinned);
      patchCustConv(c.id, { pinned: r.pinned });
    } catch (err) { alert(err?.response?.data?.message || 'Ghim thất bại'); }
  };
  if (!custConvs.length)
    return (
      <div className="chatempty">
        Chưa có khách hàng nào nhắn tin.
        <br />
        <span className="small muted">Tin nhắn từ web/app khách hàng sẽ tự hiện ở đây.</span>
      </div>
    );
  if (kw && !groups.length) return <div className="chatempty">Không có khách hàng nào khớp “{q.trim()}”.</div>;
  return (
    <>
      <div className="cust-filter-bar">
        {canSeeSale && saleOptions.length > 0 && (
          <select className="cust-filter-sale" value={filterSaleUser} onChange={(e) => setFilterSaleUser(e.target.value)} title="Lọc tin nhắn theo nhân viên phụ trách">
            <option value="">👤 Tất cả nhân viên</option>
            {saleOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <button type="button" className={'cust-filter-unread' + (unreadOnly ? ' on' : '')} onClick={() => setUnreadOnly((v) => !v)} title="Chỉ hiện khách có tin chưa đọc">
          <span className="cust-filter-dot" /> Chưa đọc
        </button>
        {isGD && (
          <button type="button" className="cust-filter-gear" onClick={() => setShowStar(true)} title="Ngưỡng đánh dấu sao khách hàng (Giám đốc)">⚙️</button>
        )}
      </div>
      {showStar && <StarThresholdModal onClose={(saved) => { setShowStar(false); if (saved) refreshCust?.(); }} />}
      <div className="chatlist" onPointerUp={onListPointerUp} onPointerCancel={() => { pressRef.current = null; }}>
      {!groups.length && (
        <div className="chatempty" style={{ padding: '18px 14px' }}>
          {unreadOnly ? 'Không có tin nào chưa đọc.' : 'Không có khách hàng nào khớp bộ lọc.'}
        </div>
      )}
      {groups.map((g) => {
        const rep = g.rep;
        const multi = g.list.length > 1;
        const draftText = g.list.map((c) => drafts?.[c.id]).find(Boolean); // nháp ở kênh bất kỳ của khách này
        const opened = g.list.some((c) => windows.some((w) => String(w.id) === String(c.id)));
        return (
          <div
            key={g.key}
            className={'chatlist-row' + (g.unread ? ' unread' : '') + (opened ? ' active' : '')}
            onPointerDown={(e) => { pressRef.current = { rep, t: Date.now(), x: e.clientX, y: e.clientY }; }}
          >
            <div className="chatlist-ava">
              <div className="chat-avatar cust">{initials(g.name)}</div>
              {g.unread > 0 && <span className="chatlist-dot" />}
            </div>
            <div className="chatlist-main">
              <div className="chatlist-top">
                <span className="chatlist-name">
                  {g.pinned ? '📌 ' : ''}
                  {g.starred && <span className="chatlist-star" title="KH doanh thu cao (được đánh dấu sao)">⭐</span>}
                  {g.name}
                  {admin && !multi && !rep.assignedTo && rep.svcType === 'kd' && <span className="chattag-warn">chưa gán</span>}
                </span>
                {draftText
                  ? <span className="chatlist-time" style={{ color: 'var(--danger)', fontWeight: 600 }}>Chưa gửi</span>
                  : <span className="chatlist-time">{fmtTime(g.lastMessage?.at)}</span>}
              </div>
              {canSeeSale && g.custSaleName && String(g.custSaleUser || '') !== myId && (
                <div className="chatlist-sale">Sale: {g.custSaleName}</div>
              )}
              <div className="chatlist-preview">
                {multi && <span className="small muted">{SVC_SHORT[rep.svcType] || 'KD'}: </span>}
                {draftText || g.lastMessage?.text || 'Bắt đầu trò chuyện…'}
              </div>
              <ChannelChips group={g} />
            </div>
            {g.unread > 0 && <span className="chatlist-badge">{g.unread}</span>}
            <button type="button" className={'chatlist-pinbtn' + (g.pinned ? ' on' : '')}
              title={g.pinned ? 'Bỏ ghim' : 'Ghim lên đầu (tối đa 3)'}
              onPointerDown={(e) => { e.stopPropagation(); pressRef.current = null; }}
              onClick={(e) => togglePinConv(e, rep)}>📌</button>
          </div>
        );
      })}
      </div>
    </>
  );
}

// Dấu tích trạng thái tin NV gửi cho KH (kiểu Telegram), đè ở GÓC DƯỚI-PHẢI bong bóng:
//   đang gửi / chờ nối bidnhanh -> đồng hồ; 'sent' (đã tới bidnhanh) -> 1 tích;
//   'read' (KH đã nhận/đọc) -> 2 tích; lỗi thật -> dấu cảnh báo.
function tickState(m) {
  if (m.direction !== 'out') return null;
  if (m.pending || m.status === 'sending') return 'sending';
  if (m.status === 'read') return 'read';
  if (m.status === 'sent') return 'sent';
  if (m.status === 'failed') return m.error === 'not-configured' ? 'sending' : 'failed';
  return 'sending';
}
// Diễn giải LÝ DO gửi lỗi (m.error từ 3dfreezy/bidnhanh) cho NV hiểu — thay vì chỉ thấy dấu "!".
export function failReason(err) {
  const e = String(err || '').toLowerCase();
  if (!e) return 'Gửi lỗi (không rõ lý do)';
  if (e.includes('timeout')) return 'Hệ thống chat KH không phản hồi kịp (quá thời gian chờ)';
  if (e.includes('offline') || e.includes('bridge')) return 'Chưa kết nối được hệ thống chat KH (3dfreezy)';
  if (e.includes('socson') || e.includes('not found') || e.includes('map') || e.includes('khách') || e.includes('customer')) return 'Chưa liên kết được khách trên hệ thống chat — thường do KH chưa mở/không có kênh chat này';
  if (e === 'rejected') return 'Hệ thống chat KH từ chối tin (không kèm lý do)';
  if (e.startsWith('http')) return 'Lỗi máy chủ chat KH (' + err + ')';
  return 'Gửi lỗi: ' + err;
}
function DeliveryTick({ m }) {
  const st = tickState(m);
  if (!st) return null;
  const cfg = {
    sending: { c: '✓', cls: 'pending', title: 'Đang gửi…' },
    sent: { c: '✓', cls: 'sent', title: 'Đã gửi cho khách' },
    read: { c: '✓✓', cls: 'read', title: 'Khách đã nhận / đã đọc' },
    failed: { c: '!', cls: 'failed', title: failReason(m.error) + ' — bấm "Gửi lại" dưới tin' },
  }[st];
  return <span className={'chatmsg-tick ' + cfg.cls} title={cfg.title}>{st === 'sending' ? '🕓' : cfg.c}</span>;
}

// Gộp danh sách tin từ server với state hiện tại — GIỮ tin đang gửi/gửi lỗi (clientId, pending/failed) và
// tin cũ đang đọc chưa có trong lô server. Dùng cho CẢ lần tải đầu lẫn poll dự phòng để đổi kênh/tải lại
// KHÔNG làm mất bong bóng "đang gửi"/"gửi lỗi" (không mất nút Gửi lại). Trả prev nếu không đổi (khỏi render lại).
function custMergeLatest(prev, msgs) {
  const serverIds = new Set(msgs.map((m) => String(m._id)));
  const pending = prev.filter((m) => m.clientId && (m.pending || m.failed) && !serverIds.has(String(m._id)));
  const oldestSrv = msgs.length ? new Date(msgs[0].createdAt).getTime() : 0;
  // GIỮ tin cũ đã tải; nhận diện tin tạm bằng pending/failed (không bằng clientId — server trả clientId cho
  // cả tin thật, trước lọc "!m.clientId" làm tin thật có clientId bị rớt mỗi nhịp poll).
  const olderKept = prev.filter((m) => !m.pending && !m.failed && !serverIds.has(String(m._id)) && m.createdAt && new Date(m.createdAt).getTime() < oldestSrv);
  const next = [...olderKept, ...msgs, ...pending];
  if (next.length === prev.length && next.every((m, i) => String(m._id) === String(prev[i]?._id))) return prev;
  return next;
}

// THU HỒI được không: tin của CHÍNH mình gửi cho KH (direction 'out'), đã gửi xong, chưa thu hồi, trong 10'.
const CUST_RECALL_WINDOW_MS = 10 * 60 * 1000;
const canRecallCust = (m, myId) => !m.recalled && !m.pending && !m.failed && m.direction === 'out'
  && String(m.sender?._id) === String(myId) && Date.now() - new Date(m.createdAt).getTime() < CUST_RECALL_WINDOW_MS;

// Cửa sổ chat với 1 KH — cùng kiểu cửa sổ chat nội bộ.
// onTitleClick (chỉ ChatPage truyền): bấm TÊN KH trên đầu khung -> mở/đóng bảng thông tin bên phải.
export function CustomerChatWindow({ conv, front, onTitleClick, onBack, onSwitchChannel }) {
  const { me, custConvs, openConv, subscribeMessages, setConvMinimized, patchCustConv, bumpCustConv, closeWindow, setOpen, setDraft, setComposerFocused, wakeSignal } = useChat();
  // Các KÊNH của cùng khách (để hiện tab chuyển kênh). switchChannel: ChatPage đổi hội thoại đang xem,
  // widget mở cửa sổ kênh khác.
  const chans = channelsOfSameCustomer(conv, custConvs);
  const switchChannel = onSwitchChannel || openConv;
  const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 980;
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(true);
  const [hasOlder, setHasOlder] = useState(false); // còn tin cũ hơn để tải (cuộn lên)
  const [loadingOlder, setLoadingOlder] = useState(false);
  const restoreOlderRef = useRef(null); // khoảng cách-tới-đáy cần khôi phục sau khi chèn tin cũ lên đầu
  // Giữ NỘI DUNG ĐANG SOẠN DỞ theo từng hội thoại KH (đổi khách quay lại vẫn còn + hiện "Chưa gửi").
  const [text, setText] = useState(() => { try { return localStorage.getItem('ft_draft_' + conv.id) || ''; } catch { return ''; } });
  // Textarea DÙNG UNCONTROLLED (defaultValue + ref): React KHÔNG bao giờ tự ghi 'value' vào DOM,
  // nên khi cửa sổ re-render (tin đến / "đang gõ" / tick) giữa lúc gõ dấu, IME (Telex) KHÔNG bị cắt
  // -> hết "gõ tiếng Việt mất dấu". State `text` vẫn phản ánh nội dung (nút Gửi + nháp) qua onChange.
  const initialTextRef = useRef(null); // nháp lúc MOUNT -> defaultValue
  if (initialTextRef.current === null) initialTextRef.current = text;
  const [atts, setAtts] = useState([]);
  const [replyTo, setReplyTo] = useState(null); // { id, senderName, text }
  const [imgView, setImgView] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [pastePrompt, setPastePrompt] = useState(null); // { imgs, text } — dán Excel: hỏi gửi ảnh hay chữ
  const [minimized, setMinimized] = useState(false);
  const [minUnread, setMinUnread] = useState(0);
  const [reactPickerFor, setReactPickerFor] = useState(null);
  // Bảng cảm xúc: đóng khi bấm ra ngoài (không đóng khi di chuột) — bỏ onMouseLeave để bấm được emoji.
  useEffect(() => {
    if (!reactPickerFor) return;
    const close = (e) => { if (!e.target.closest('.chatmsg-react-picker')) setReactPickerFor(null); };
    const t = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { clearTimeout(t); document.removeEventListener('click', close); };
  }, [reactPickerFor]);
  // GIỮ (long-press) trên mobile = mở menu nhỏ (Trả lời / Sao chép); chạm nhanh = thả cảm xúc.
  const [actionFor, setActionFor] = useState(null);
  // CÔNG VIỆC (chỉ NV thấy): tin đang thao tác + danh sách nhân viên để giao + bảng công việc (mở cả mobile).
  const [taskMsg, setTaskMsg] = useState(null);
  const [taskStaff, setTaskStaff] = useState([]);
  const [showTasks, setShowTasks] = useState(false);
  useEffect(() => { chatUsers().then((us) => setTaskStaff((us || []).map((u) => ({ id: u.id, name: u.name })))).catch(() => {}); }, []);
  const liveCust = custConvs.find((c) => String(c.id) === String(conv.id)) || conv;
  const custTasks = liveCust.tasks || [];
  const patchTasks = useCallback((next) => patchCustConv(conv.id, { tasks: next }), [conv.id, patchCustConv]);
  const createTaskFromMsg = useCallback(async (payload) => {
    const r = await createCustTask(conv.id, payload);
    patchTasks([...(liveCust.tasks || []), r.task]);
    toast('📋 Đã tạo công việc — chỉ nhân viên thấy');
  }, [conv.id, liveCust.tasks, patchTasks, toast]);
  const taskAccept = useCallback(async (taskId) => {
    try { const r = await acceptCustTask(conv.id, taskId); patchTasks((liveCust.tasks || []).map((t) => (t.id === taskId ? r.task : t))); }
    catch (e) { toast(e?.response?.data?.message || 'Không tiếp nhận được', 'danger'); }
  }, [conv.id, liveCust.tasks, patchTasks, toast]);
  const taskComplete = useCallback(async (taskId, done) => {
    try { const r = await completeCustTask(conv.id, taskId, done); patchTasks((liveCust.tasks || []).map((t) => (t.id === taskId ? r.task : t))); }
    catch (e) { toast(e?.response?.data?.message || 'Không cập nhật được', 'danger'); }
  }, [conv.id, liveCust.tasks, patchTasks, toast]);
  const taskDelete = useCallback(async (taskId) => {
    if (!window.confirm('Xoá công việc này?')) return;
    try { await deleteCustTask(conv.id, taskId); patchTasks((liveCust.tasks || []).filter((t) => t.id !== taskId)); }
    catch (e) { toast(e?.response?.data?.message || 'Xoá thất bại', 'danger'); }
  }, [conv.id, liveCust.tasks, patchTasks, toast]);
  useEffect(() => {
    if (!actionFor) return;
    const close = (e) => { if (!e.target.closest('.chatmsg-action-menu')) setActionFor(null); };
    const t = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { clearTimeout(t); document.removeEventListener('click', close); };
  }, [actionFor]);
  // SAO CHÉP nội dung tin (cần trên mobile — long-press nuốt mất "chọn chữ + copy" của hệ thống).
  const doCopy = useCallback(async (m) => {
    const t = (m.text || '').trim();
    if (!t) { toast('Tin này không có chữ để sao chép', 'danger'); return; }
    const legacy = () => {
      try {
        const ta = document.createElement('textarea');
        ta.value = t; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.top = '0'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select(); ta.setSelectionRange(0, t.length);
        const ok = document.execCommand('copy'); document.body.removeChild(ta); return ok;
      } catch { return false; }
    };
    try { await navigator.clipboard.writeText(t); toast('📋 Đã sao chép'); return; } catch { /* thử cách cũ */ }
    const ok = legacy();
    toast(ok ? '📋 Đã sao chép' : 'Không sao chép được — thử giữ tin rồi chọn Sao chép', ok ? undefined : 'danger');
  }, [toast]);
  const lpTimer = useRef(null);
  const lpFired = useRef(false);
  const [peek, setPeek] = useState(null);
  const [newBelow, setNewBelow] = useState(0);
  const [scrolledUp, setScrolledUp] = useState(false); // cuộn xa đáy -> luôn cho nút về tin mới nhất
  const [typingNames, setTypingNames] = useState([]); // NV khác đang gõ (staff↔staff, tránh trả lời trùng)
  const typingSentRef = useRef(false);
  const typingStopRef = useRef(null);
  const typingInRef = useRef({});
  const [size, setSize] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('ft_chat_size') || 'null');
      if (s && s.w && s.h) return { w: s.w, h: s.h };
    } catch {}
    return null;
  });
  const emojiBtnRef = useRef(null);
  const minimizedRef = useRef(false);
  const endRef = useRef(null);
  const msgsRef = useRef(null);
  const lastMsgIdRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const unreadRef = useRef(null);
  const initRef = useRef(false);
  const stickBottomRef = useRef(true); // ĐANG "dính đáy"? -> ảnh/biểu đồ tải xong tự cuộn lại đáy (không trôi lên)
  const unreadSnapRef = useRef(conv.unread || 0);

  const myId = String(me.id || me._id);

  const toggleMinimize = useCallback((next) => {
    const on = typeof next === 'boolean' ? next : !minimizedRef.current;
    minimizedRef.current = on;
    setMinimized(on);
    setConvMinimized(conv.id, on);
    if (!on) { setMinUnread(0); import('../api/customerChat').then(({ custMarkRead }) => custMarkRead(conv.id).catch(() => {})); }
  }, [conv.id, setConvMinimized]);
  useEffect(() => () => setConvMinimized(conv.id, false), [conv.id, setConvMinimized]);
  useEffect(() => () => setComposerFocused(conv.id, false), [conv.id, setComposerFocused]);

  // Tải tin khi mở cửa sổ.
  useEffect(() => {
    let alive = true;
    setLoadingMsgs(true);
    initRef.current = false;
    unreadSnapRef.current = conv.unread || 0;
    listCustMessages(conv.id, { limit: 60 }) // 60 tin mới nhất (trước 300 -> mở CHẬM); tin cũ tải bằng "Xem tin cũ hơn"
      // GỘP thay vì thay sạch: đổi kênh/mở lại KHÔNG vứt bong bóng "đang gửi/gửi lỗi" (giữ nút Gửi lại).
      .then((msgs) => { if (alive) { setMessages((prev) => custMergeLatest(prev, msgs)); setHasOlder((msgs || []).length >= 60); } })
      .finally(() => alive && setLoadingMsgs(false));
    return () => { alive = false; };
  }, [conv.id]);

  // reveal=true (BẤM NÚT "Xem tin cũ hơn"): cuộn LÊN để HIỆN tin cũ vừa nạp. reveal=false (CUỘN TAY gần đỉnh):
  // GIỮ NGUYÊN chỗ đọc để cuộn vô tận mượt (tin cũ chèn lên trên, tự lộ ra khi cuộn tiếp).
  const loadOlder = useCallback(async (reveal = false) => {
    if (loadingOlder) return;
    // Mốc "tin cổ nhất": tin ĐÃ XÁC NHẬN (không phải tin TẠM đang gửi/lỗi). Server lưu & trả kèm clientId nên
    // KHÔNG coi "có clientId = tin tạm" (sẽ bỏ qua mọi tin -> oldest=null -> không tải được tin cũ). Nhận diện
    // tin tạm bằng pending/failed; lấy createdAt nhỏ nhất (phòng lệch thứ tự).
    const oldest = messages.reduce(
      (min, m) => (m.createdAt && !m.pending && !m.failed && (!min || new Date(m.createdAt) < new Date(min.createdAt)) ? m : min),
      null,
    );
    if (!oldest) return;
    setLoadingOlder(true);
    try {
      const older = await listCustMessages(conv.id, { before: oldest.createdAt });
      const el = msgsRef.current;
      // Chốt số đo TRƯỚC khi chèn; điều chỉnh scrollTop ở useLayoutEffect (SAU khi DOM có tin mới, số đo đúng).
      restoreOlderRef.current = el ? { keep: el.scrollHeight - el.scrollTop, oldSH: el.scrollHeight, reveal } : null;
      setMessages((prev) => {
        const have = new Set(prev.map((m) => String(m._id)));
        return [...(older || []).filter((m) => !have.has(String(m._id))), ...prev];
      });
      setHasOlder((older || []).length >= 40);
    } catch { /* mạng lỗi -> bấm lại */ }
    finally { setLoadingOlder(false); }
  }, [conv.id, messages, loadingOlder]);

  // Điều chỉnh scrollTop SAU khi chèn tin cũ (DOM đã có tin mới -> số đo đúng -> không giật).
  useLayoutEffect(() => {
    const info = restoreOlderRef.current;
    if (info == null) return;
    const el = msgsRef.current;
    if (el) {
      if (info.reveal) {
        // BẤM NÚT: lộ các tin cũ vừa nạp, giữ tin đang đọc ở mép dưới cho liền mạch.
        const prepended = el.scrollHeight - info.oldSH;
        el.scrollTop = Math.max(0, prepended - el.clientHeight + 60);
      } else {
        // CUỘN TAY: giữ NGUYÊN chỗ đang đọc.
        el.scrollTop = el.scrollHeight - info.keep;
      }
    }
    restoreOlderRef.current = null;
  }, [messages]);

  // Tin mới realtime (qua bus của ChatContext — sự kiện custchat:message).
  useEffect(() => {
    const unsub = subscribeMessages(conv.id, (payload) => {
      if (minimizedRef.current && payload.direction === 'in' && !payload.clientId) setMinUnread((n) => n + 1);
      setMessages((prev) => {
        if (payload.clientId && prev.some((m) => m.clientId && m.clientId === payload.clientId)) {
          return prev.map((m) => (m.clientId === payload.clientId ? { ...payload } : m));
        }
        return prev.some((m) => String(m._id) === String(payload._id)) ? prev : [...prev, payload];
      });
    });
    return unsub;
  }, [conv.id, subscribeMessages]);

  // DỰ PHÒNG socket chập chờn: ~5s tải lại + gộp (giữ tin đang gửi/lỗi) — giống chat nội bộ.
  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      listCustMessages(conv.id, { limit: 40 }) // chỉ 40 tin mới nhất (đủ bắt tin mới); trước tải 300 tin/lần -> nặng
        .then((msgs) => setMessages((prev) => custMergeLatest(prev, msgs)))
        .catch(() => {});
    };
    const t = setInterval(tick, 2500); // 2.5s: cắt trễ khi socket lỡ event (trước 5s) — chat KH mượt hơn
    return () => clearInterval(t);
  }, [conv.id]);

  // TAB HIỆN LẠI / SOCKET NỐI LẠI (wakeSignal bump) -> tải bù tin KH NGAY, không đợi tick 2.5s.
  useEffect(() => {
    if (!wakeSignal) return;
    listCustMessages(conv.id, { limit: 40 })
      .then((msgs) => setMessages((prev) => custMergeLatest(prev, msgs)))
      .catch(() => {});
  }, [wakeSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Đang gõ" (staff↔staff): báo bật 1 lần, tự tắt sau 2,5s ngừng gõ.
  const notifyCustTyping = useCallback((on) => {
    if (on) {
      if (!typingSentRef.current) { typingSentRef.current = true; emitCustTyping(conv.id, true); }
      clearTimeout(typingStopRef.current);
      typingStopRef.current = setTimeout(() => { typingSentRef.current = false; emitCustTyping(conv.id, false); }, 2500);
    } else {
      clearTimeout(typingStopRef.current);
      if (typingSentRef.current) { typingSentRef.current = false; emitCustTyping(conv.id, false); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.id]);

  // Vào "phòng" hội thoại đang mở + nhận "đang gõ" của NV khác.
  useEffect(() => {
    emitCustView(conv.id);
    const socket = getSocket();
    const onTyping = (evt) => {
      if (String(evt.conversation) !== String(conv.id)) return;
      if (String(evt.from) === myId) return;
      const nm = evt.name || 'Nhân viên';
      if (evt.typing) {
        setTypingNames((prev) => (prev.includes(nm) ? prev : [...prev, nm]));
        clearTimeout(typingInRef.current[nm]);
        typingInRef.current[nm] = setTimeout(() => { setTypingNames((prev) => prev.filter((x) => x !== nm)); delete typingInRef.current[nm]; }, 4000);
      } else {
        clearTimeout(typingInRef.current[nm]); delete typingInRef.current[nm];
        setTypingNames((prev) => prev.filter((x) => x !== nm));
      }
    };
    socket?.on('custchat:typing', onTyping);
    return () => {
      socket?.off('custchat:typing', onTyping);
      notifyCustTyping(false);
      emitCustView(null);
      Object.values(typingInRef.current).forEach(clearTimeout);
      typingInRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.id]);

  // Cảm xúc realtime.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onReaction = (p) => {
      if (String(p.conversation) !== String(conv.id)) return;
      setMessages((prev) => prev.map((m) => (String(m._id) === String(p.messageId) ? { ...m, reactions: p.reactions } : m)));
    };
    // Trạng thái giao tin đổi (KH đã đọc -> 'read' = 2 tích).
    const onStatus = (p) => {
      if (String(p.conversation) !== String(conv.id)) return;
      const ids = new Set((p.messageIds || []).map(String));
      setMessages((prev) => prev.map((m) => (ids.has(String(m._id)) ? { ...m, status: p.status } : m)));
    };
    // NV khác thu hồi tin -> cập nhật ngay thành "đã thu hồi".
    const onRecall = (p) => {
      if (String(p.conversation) !== String(conv.id)) return;
      setMessages((prev) => prev.map((m) => (String(m._id) === String(p._id) ? { ...m, recalled: true, text: '', attachments: [], replyTo: null } : m)));
    };
    socket.on('custchat:reaction', onReaction);
    socket.on('custchat:status', onStatus);
    socket.on('custchat:recall', onRecall);
    return () => { socket.off('custchat:reaction', onReaction); socket.off('custchat:status', onStatus); socket.off('custchat:recall', onRecall); };
  }, [conv.id]);

  const isNearBottom = () => {
    const el = msgsRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };
  const jumpToLatest = () => {
    const el = msgsRef.current;
    stickBottomRef.current = true; // muốn ở đáy -> ảnh tải xong sau đó vẫn giữ ở đáy
    if (el) el.scrollTop = el.scrollHeight;
    setNewBelow(0);
  };

  // Lưu bản nháp theo hội thoại KH (chuyển khách không mất đoạn đang gõ + hiện "Chưa gửi" ở danh sách).
  useEffect(() => { setDraft(conv.id, text); }, [text, conv.id, setDraft]);

  // Cuộn: lần đầu tới tin chưa đọc; tin mới chỉ tự cuộn khi của mình hoặc đang ở gần đáy.
  useEffect(() => {
    if (!messages.length) return;
    const last = messages[messages.length - 1];
    const lastId = String(last.clientId || last._id);
    if (!initRef.current) {
      initRef.current = true;
      lastMsgIdRef.current = lastId;
      // LUÔN nhảy xuống TIN MỚI NHẤT khi mở hội thoại (không dừng ở tin chưa đọc cũ tít trên). Cuộn lại vài
      // nhịp vì ảnh/nội dung giãn ra + content-visibility ước lượng chiều cao làm lệch đáy lúc đầu.
      jumpToLatest();
      setTimeout(jumpToLatest, 60);
      setTimeout(jumpToLatest, 300);
      return;
    }
    if (lastMsgIdRef.current === lastId) return;
    lastMsgIdRef.current = lastId;
    if (String(last.sender?._id || '') === myId || isNearBottom()) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      setNewBelow((n) => n + 1);
    }
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (atts.length) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [atts]); // eslint-disable-line react-hooks/exhaustive-deps

  // ẢNH tải xong -> khung cao lên; nếu đang DÍNH ĐÁY thì kéo lại đáy (chống trôi lên khi mở chat nhiều ảnh).
  // Bắt 'load' của <img> bằng capture ('load' không bubble).
  useEffect(() => {
    const el = msgsRef.current;
    if (!el) return;
    const onLoadCapture = () => { if (stickBottomRef.current) el.scrollTop = el.scrollHeight; };
    el.addEventListener('load', onLoadCapture, true);
    return () => el.removeEventListener('load', onLoadCapture, true);
  }, [conv.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ô nhập tự cao theo nội dung (tối đa ~5 dòng) — giống chat nội bộ.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 112) + 'px';
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  function addFiles(fileList) {
    const items = [...fileList].map((f) => ({
      file: f, name: f.name, type: f.type, size: f.size,
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }));
    setAtts((prev) => [...prev, ...items]);
  }
  function onPaste(e) {
    const imgs = [...(e.clipboardData?.items || [])].filter((it) => it.kind === 'file').map((it) => it.getAsFile()).filter(Boolean);
    const txt = e.clipboardData?.getData('text/plain') || '';
    // Dán từ Excel/bảng (có CẢ ảnh CẢ chữ) -> hỏi gửi dạng nào (giống Zalo).
    if (imgs.length && txt.trim()) { e.preventDefault(); setPastePrompt({ imgs, text: txt }); return; }
    if (imgs.length) { e.preventDefault(); addFiles(imgs); }
  }
  const choosePasteImage = () => { if (pastePrompt) addFiles(pastePrompt.imgs); setPastePrompt(null); };
  const choosePasteText = () => { const p = pastePrompt; setPastePrompt(null); if (p) insertEmoji(p.text); };
  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }

  // Đóng bảng emoji khi click ra ngoài.
  useEffect(() => {
    if (!showEmoji) return;
    const onDown = (e) => {
      if (e.target.closest?.('.emoji-pop')) return;
      if (emojiBtnRef.current?.contains(e.target)) return;
      setShowEmoji(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showEmoji]);

  // Đổi nội dung ô soạn BẰNG CODE (uncontrolled -> phải tự ghi DOM rồi đồng bộ state).
  const putText = useCallback((v) => {
    const el = inputRef.current;
    if (el) el.value = v;
    setText(v);
  }, []);

  const insertEmoji = useCallback((emo) => {
    const el = inputRef.current;
    const cur = el ? el.value : text;
    const a = el ? el.selectionStart : cur.length;
    const b = el ? el.selectionEnd : cur.length;
    const next = cur.slice(0, a) + emo + cur.slice(b);
    putText(next);
    setTimeout(() => {
      el?.focus();
      const p = a + emo.length;
      el?.setSelectionRange(p, p);
    }, 0);
  }, [text, putText]);

  const sendSticker = useCallback(async (emo) => {
    setShowEmoji(false);
    try {
      const msg = await sendCustReply(conv.id, emo, {});
      setMessages((prev) => (prev.some((m) => String(m._id) === String(msg._id)) ? prev : [...prev, msg]));
      bumpCustConv(conv.id, msg);
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || 'Gửi sticker thất bại');
    }
  }, [conv.id, bumpCustConv]);

  async function submit(e) {
    e?.preventDefault();
    const t = (inputRef.current?.value ?? text).trim(); // đọc trực tiếp DOM (uncontrolled) cho chắc
    if ((!t && !atts.length) || sending) return;
    const reply = replyTo;
    const replySnap = reply ? { message: reply.id, senderName: reply.senderName, text: reply.text } : null;
    const sentAtts = atts;
    putText('');
    setAtts([]);
    setReplyTo(null);

    // Đường nhanh: chỉ chữ -> hiện ngay (optimistic), thay bằng bản thật khi server trả về.
    if (!sentAtts.length) {
      const clientId = 'tmp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      const temp = { _id: clientId, clientId, direction: 'out', sender: { _id: myId, name: me.name }, senderName: me.name, text: t, attachments: [], replyTo: replySnap, createdAt: new Date().toISOString(), pending: true };
      setMessages((prev) => [...prev, temp]);
      try {
        const msg = await sendCustReply(conv.id, t, { clientId, replyTo: reply?.id });
        setMessages((prev) => {
          if (prev.some((m) => String(m._id) === String(msg._id) && m.clientId !== clientId)) {
            return prev.filter((m) => m.clientId !== clientId);
          }
          return prev.map((m) => (m.clientId === clientId ? { ...msg } : m));
        });
        bumpCustConv(conv.id, msg);
      } catch {
        setMessages((prev) => prev.map((m) => (m.clientId === clientId ? { ...m, pending: false, failed: true } : m)));
      }
      return;
    }

    // Có đính kèm: hiện NGAY (optimistic) bằng preview cục bộ, upload ngầm lên MEDIA SERVER dùng chung
    // (tệp này khách bên bidnhanh phải xem được), rồi thay bằng bản thật.
    const clientId = 'tmp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    const tempAtts = sentAtts.map((a) => ({ url: a.preview || '', name: a.file.name, type: a.file.type, size: a.file.size }));
    const temp = { _id: clientId, clientId, direction: 'out', sender: { _id: myId, name: me.name }, senderName: me.name, text: t, attachments: tempAtts, replyTo: replySnap, createdAt: new Date().toISOString(), pending: true };
    setMessages((prev) => [...prev, temp]);
    try {
      const metas = await uploadCustomerChatFiles(sentAtts.map((a) => a.file));
      const msg = await sendCustReply(conv.id, t, { attachments: metas, clientId, replyTo: reply?.id });
      setMessages((prev) => {
        if (prev.some((m) => String(m._id) === String(msg._id) && m.clientId !== clientId)) return prev.filter((m) => m.clientId !== clientId);
        return prev.map((m) => (m.clientId === clientId ? { ...msg } : m));
      });
      bumpCustConv(conv.id, msg);
      sentAtts.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
    } catch {
      setMessages((prev) => prev.map((m) => (m.clientId === clientId ? { ...m, pending: false, failed: true, _retryFiles: sentAtts } : m)));
    }
  }

  const resend = useCallback(async (failed) => {
    setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId ? { ...m, pending: true, failed: false } : m)));
    try {
      let msg;
      if (failed._retryFiles && failed._retryFiles.length) {
        const metas = await uploadCustomerChatFiles(failed._retryFiles.map((a) => a.file));
        msg = await sendCustReply(conv.id, failed.text || '', { attachments: metas, clientId: failed.clientId });
        failed._retryFiles.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
      } else {
        msg = await sendCustReply(conv.id, failed.text, { clientId: failed.clientId });
      }
      setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId ? { ...msg } : m)));
      bumpCustConv(conv.id, msg);
    } catch {
      setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId ? { ...m, pending: false, failed: true } : m)));
    }
  }, [conv.id, bumpCustConv]);

  // Tắt/bật âm hội thoại KH (riêng mình).
  const muted = !!conv.muted;
  const doMute = useCallback(async () => {
    try {
      const r = await custMute(conv.id, !muted);
      patchCustConv(conv.id, { muted: r.muted });
      toast(r.muted ? '🔕 Đã tắt âm hội thoại này' : '🔔 Đã bật lại thông báo');
    } catch (e) { toast(e?.response?.data?.message || 'Không đổi được', 'danger'); }
  }, [conv.id, muted, patchCustConv, toast]);

  const startReply = useCallback((m) => {
    const preview = m.text || ((m.attachments || []).length ? 'đã gửi tệp đính kèm' : '');
    setReplyTo({ id: m._id, senderName: m.direction === 'in' ? (m.senderName || conv.name) : (m.sender?.name || m.senderName || ''), text: String(preview).slice(0, 200) });
    setTimeout(() => {
      const el = inputRef.current;
      if (el) { el.focus(); const n = el.value.length; el.setSelectionRange(n, n); }
    }, 0);
  }, [conv.name]);

  // GỬI LẠI tin ĐÃ LƯU nhưng hệ chat KH từ chối (m.status === 'failed'). Server đẩy lại ĐÚNG tin cũ
  // (giữ outboundId) nên khách KHÔNG nhận trùng. Khác resend() ở trên: resend dùng cho tin CHƯA tới được
  // server FunnyTask (mất mạng giữa chừng) nên phải gửi thành tin mới.
  const [retrying, setRetrying] = useState('');
  const doRetry = useCallback(async (m) => {
    setRetrying(String(m._id));
    try {
      const updated = await custRetryMessage(conv.id, m._id);
      setMessages((prev) => prev.map((x) => (String(x._id) === String(m._id) ? { ...x, ...updated } : x)));
      if (updated.status !== 'sent' && updated.status !== 'read') toast(failReason(updated.error), 'danger');
    } catch (err) {
      toast(err?.response?.data?.message || 'Gửi lại thất bại', 'danger');
    } finally { setRetrying(''); }
  }, [conv.id, toast]);

  // THU HỒI tin của mình gửi KH (trong 10'): server xoá nội dung, hiện "Tin nhắn đã được thu hồi" cho mọi NV
  // + báo 3dfreezy gỡ phía khách. Chỉ tin 'out' của CHÍNH mình, đã gửi xong, chưa thu hồi.
  const doRecall = useCallback(async (m) => {
    if (!window.confirm('Thu hồi tin nhắn này? Khách và mọi nhân viên sẽ thấy "Tin nhắn đã được thu hồi".')) return;
    try {
      await custRecallMessage(conv.id, m._id);
      setMessages((prev) => prev.map((x) => (String(x._id) === String(m._id) ? { ...x, recalled: true, text: '', attachments: [], replyTo: null } : x)));
    } catch (err) { alert(err?.response?.data?.message || 'Thu hồi thất bại'); }
  }, [conv.id]);

  // Thả/đổi/bỏ cảm xúc (chỉ tin không phải của mình) — cập nhật lạc quan.
  const doReact = useCallback((m, emoji) => {
    setReactPickerFor(null);
    setMessages((prev) => prev.map((mm) => {
      if (String(mm._id) !== String(m._id)) return mm;
      const others = (mm.reactions || []).filter((r) => String(r.user) !== myId);
      const cur = (mm.reactions || []).find((r) => String(r.user) === myId);
      const next = !emoji || (cur && cur.emoji === emoji) ? others : [...others, { user: myId, name: me.name, emoji }];
      return { ...mm, reactions: next };
    }));
    custReact(m._id, emoji).catch(() => listCustMessages(conv.id).then(setMessages).catch(() => {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myId, me.name, conv.id]);

  const scrollToMsg = useCallback((id) => {
    const el = id && document.getElementById('kmsg-' + id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('chatmsg-flash');
    setTimeout(() => el.classList.remove('chatmsg-flash'), 1400);
  }, []);

  // Nhảy tới 1 tin từ ô "Tìm trong đoạn chat" (panel bên).
  useEffect(() => {
    const onJump = (e) => { if (String(e.detail?.convId) === String(conv.id)) scrollToMsg(e.detail.id); };
    window.addEventListener('ft:jump-msg', onJump);
    return () => window.removeEventListener('ft:jump-msg', onJump);
  }, [conv.id, scrollToMsg]);

  // Tin KH chưa đọc đầu tiên (để kẻ vạch "Tin nhắn mới").
  const firstUnreadId = (() => {
    const n = unreadSnapRef.current;
    if (!n) return null;
    const incoming = messages.filter((m) => m.direction === 'in');
    if (!incoming.length) return null;
    return incoming[Math.max(0, incoming.length - n)]?._id || null;
  })();

  const messageEls = useMemo(
    () =>
      messages.map((m, _mi) => {
        // TIN HỆ THỐNG (thông báo công việc) — CHỈ nhân viên thấy: dòng căn giữa, không avatar/menu/cảm xúc.
        if (m.system) {
          return (
            <div className="chatmsg-row chatmsg-sysrow" key={m._id} id={'kmsg-' + m._id}>
              <div className="chat-sysmsg"><span>{m.text}</span></div>
            </div>
          );
        }
        // MỌI tin nhân viên (direction 'out') về BÊN PHẢI — dù là mình hay NV khác — cho nhất quán
        // "phía công ty". Tin của NV khác hiện TÊN THẬT (nội bộ + GĐ biết ai chat); tin của mình không
        // cần tên. Tin KH ở bên trái kèm tên KH. (KH bên 3dfreezy chỉ thấy nhãn phòng ban, không tên thật.)
        const staffSide = m.direction === 'out';
        const mine = staffSide && String(m.sender?._id || '') === myId;
        const isFirstUnread = String(m._id) === String(firstUnreadId);
        const who = staffSide ? (m.sender?.name || m.senderName) : (m.senderName || conv.name);
        const prevMsg = messages[_mi - 1];
        const showDate = m.createdAt && (!prevMsg || !prevMsg.createdAt || new Date(prevMsg.createdAt).toDateString() !== new Date(m.createdAt).toDateString());
        // GỘP tin nối tiếp cùng "người" (cùng phía; nếu là NV thì cùng sender) trong 5 phút -> ẩn tên, khít lại.
        const prevStaff = prevMsg && prevMsg.direction === 'out';
        const sameWho = prevMsg && prevStaff === staffSide && (staffSide ? String(prevMsg.sender?._id || '') === String(m.sender?._id || '') : true);
        const grouped = sameWho && prevMsg.createdAt && m.createdAt && (new Date(m.createdAt) - new Date(prevMsg.createdAt) < 5 * 60 * 1000) && !showDate && !isFirstUnread;
        const menuOpen = actionFor === m._id || reactPickerFor === m._id; // đang mở menu ⋯ / bảng cảm xúc
        return (
          <div className={'chatmsg-row' + (grouped ? ' grouped' : '') + (menuOpen ? ' menu-open' : '')} key={m._id} id={'kmsg-' + m._id}>
            {showDate && (
              <div className="chat-datedivider"><span>{fmtDayLabel(m.createdAt)}</span></div>
            )}
            {isFirstUnread && (
              <div className="chat-newdivider" ref={unreadRef}>
                <span>Tin nhắn mới</span>
              </div>
            )}
            <div className={'chatmsg' + (staffSide ? ' mine' : '') + (m.pending ? ' pending' : '') + (m.failed ? ' failed' : '')}>
              {/* Tên người viết: tin KH hiện tên KH (trái); tin NV KHÁC hiện tên NV (phải). Tin của mình: không tên. */}
              {!mine && !grouped && (
                <div className="chatmsg-who">{who}{m.isAuto && <span className="chatmsg-bot">🤖 tự động</span>}</div>
              )}
              <div className="chatmsg-line">
                <div className="chatmsg-content">
                  {m.replyTo && (
                    <div className="chatmsg-quote" onClick={() => scrollToMsg(m.replyTo.message)} title="Xem tin gốc">
                      <span className="chatmsg-quote-name">{m.replyTo.senderName || 'Tin nhắn'}</span>
                      <span className="chatmsg-quote-text">{m.replyTo.text || 'đã gửi tệp đính kèm'}</span>
                    </div>
                  )}
                  <div
                    className={'chatmsg-body' + (staffSide ? ' mine' : '')}
                    onClick={() => {
                      if (lpFired.current) return; // vừa GIỮ để trả lời -> bỏ qua chạm
                      if (!mine) setReactPickerFor((v) => (v === m._id ? null : m._id));
                    }}
                    onTouchStart={() => { lpFired.current = false; clearTimeout(lpTimer.current); lpTimer.current = setTimeout(() => { lpFired.current = true; setReactPickerFor(null); setActionFor(m._id); }, 420); }}
                    onTouchEnd={() => clearTimeout(lpTimer.current)}
                    onTouchMove={() => clearTimeout(lpTimer.current)}
                    title={!mine ? 'Chạm để thả cảm xúc · Giữ để mở menu' : 'Giữ để mở menu'}
                  >
                    {m.recalled ? (
                      <div className="chatmsg-bubble chatmsg-recalled">🚫 Tin nhắn đã được thu hồi</div>
                    ) : (
                      <>
                        {m.text && (
                          <div className={'chatmsg-bubble' + (isBigEmoji(m.text) ? ' big-emoji' : '') + (staffSide ? ' has-tick' : '')}>
                            {linkifyText(m.text)}
                            {staffSide && <DeliveryTick m={m} />}
                          </div>
                        )}
                        {(m.attachments || []).map((a, i) => (
                          <div className="chatmsg-att" key={i}>
                            <AttachmentView a={a} />
                          </div>
                        ))}
                        {staffSide && !m.text && (m.attachments || []).length > 0 && (
                          <div className="chatmsg-tick-float"><DeliveryTick m={m} /></div>
                        )}
                        <div className="chatmsg-corner">
                          <button className="chatmsg-cbtn" title="Trả lời tin này" onClick={(e) => { e.stopPropagation(); startReply(m); }}>↩</button>
                          <button className="chatmsg-cbtn" title="Sao chép nội dung" onClick={(e) => { e.stopPropagation(); doCopy(m); }}>📋</button>
                          {canRecallCust(m, myId) && (
                            <button className="chatmsg-cbtn" title="Thu hồi tin này" onClick={(e) => { e.stopPropagation(); doRecall(m); }}>🗑</button>
                          )}
                        </div>
                        {actionFor === m._id && (
                          <div className={'chatmsg-menu chatmsg-action-menu' + (staffSide ? ' mine' : '')} onClick={(e) => e.stopPropagation()}>
                            <button className="chatmsg-menu-item" onClick={() => { startReply(m); setActionFor(null); }}><span className="chatmsg-menu-ico">↩</span> Trả lời</button>
                            {m.text && (
                              <button className="chatmsg-menu-item" onClick={() => { doCopy(m); setActionFor(null); }}><span className="chatmsg-menu-ico">📋</span> Sao chép</button>
                            )}
                            <button className="chatmsg-menu-item" onClick={() => { setTaskMsg(m); setActionFor(null); }}><span className="chatmsg-menu-ico">📋</span> Tạo công việc</button>
                            {canRecallCust(m, myId) && (
                              <button className="chatmsg-menu-item danger" onClick={() => { doRecall(m); setActionFor(null); }}><span className="chatmsg-menu-ico">🗑</span> Thu hồi</button>
                            )}
                          </div>
                        )}
                        {!mine && reactPickerFor === m._id && (
                          <div className="chatmsg-react-picker" onClick={(e) => e.stopPropagation()}>
                            {REACTION_EMOJIS.map((e) => (
                              <button key={e} type="button" className="chatmsg-react-opt" onClick={() => doReact(m, e)}>{e}</button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {staffSide && m.status === 'failed' && m.error !== 'not-configured' && (
                    <div className="chatmsg-failnote" style={{ fontSize: 11, color: 'var(--danger, #dc2626)', marginTop: 3, lineHeight: 1.35, textAlign: 'right', maxWidth: 320 }}>
                      ⚠ {failReason(m.error)}
                      {/* KHÔNG tự gửi lại (khách dễ nhận trùng) -> người gửi tự bấm. */}
                      {mine && (
                        <> · <button type="button" className="chatmsg-resend" disabled={retrying === String(m._id)} onClick={() => doRetry(m)}>
                          {retrying === String(m._id) ? 'Đang gửi…' : 'Gửi lại'}
                        </button></>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {(m.reactions || []).length > 0 && (
                <div className="chatmsg-reacts">
                  {Object.entries(
                    (m.reactions || []).reduce((acc, r) => {
                      (acc[r.emoji] = acc[r.emoji] || []).push(r.name || 'Ai đó');
                      return acc;
                    }, {})
                  ).map(([emoji, names]) => {
                    const meReacted = (m.reactions || []).some((r) => String(r.user) === myId && r.emoji === emoji);
                    return (
                      <button
                        key={emoji}
                        type="button"
                        className={'chatmsg-react-badge' + (meReacted ? ' me' : '')}
                        title="Xem ai đã thả cảm xúc"
                        onClick={() => setPeek({ title: 'Cảm xúc', lines: (m.reactions || []).map((r) => ({ icon: r.emoji, name: r.name || 'Ai đó' })) })}
                      >
                        <span>{emoji}</span> {names.length}
                      </button>
                    );
                  })}
                </div>
              )}
              {mine && m.failed ? (
                <div className="chatmsg-time" style={{ color: 'var(--danger)' }}>
                  Gửi lỗi · <button type="button" className="chatmsg-resend" onClick={() => resend(m)}>Gửi lại</button>
                </div>
              ) : (
                <div className="chatmsg-time">{m.pending ? 'Đang gửi…' : fmtMsgTime(m.createdAt)}</div>
              )}
            </div>
          </div>
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages, myId, firstUnreadId, reactPickerFor, actionFor, doReact, doCopy, resend, startReply, doRecall, scrollToMsg]
  );

  // Kéo giãn cửa sổ (chia sẻ kích thước với chat nội bộ qua ft_chat_size).
  const startResize = useCallback((e) => {
    if (window.innerWidth <= 980) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = panelRef.current?.getBoundingClientRect();
    const startW = rect?.width || 372;
    const startH = rect?.height || 520;
    const startX = e.clientX;
    const startY = e.clientY;
    const onMove = (ev) => {
      const w = Math.min(720, Math.max(300, startW + (startX - ev.clientX)));
      const h = Math.min(Math.round(window.innerHeight * 0.92), Math.max(360, startH + (startY - ev.clientY)));
      setSize({ w, h });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      setSize((s) => { if (s) { try { localStorage.setItem('ft_chat_size', JSON.stringify(s)); } catch {} } return s; });
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  const panelStyle = size && !minimized ? { width: size.w, height: size.h } : undefined;

  return (
    <div ref={panelRef} style={panelStyle} className={'chatpanel chatpanel-conv' + (front ? ' front' : '') + (minimized ? ' min' : '')}>
      {!minimized && <div className="chatpanel-resize" onMouseDown={startResize} title="Kéo để thay đổi kích thước" />}
      {peek && (
        <div className="chat-peek-overlay" onClick={() => setPeek(null)}>
          <div className="chat-peek" onClick={(e) => e.stopPropagation()}>
            <div className="chat-peek-head">
              <b>{peek.title} · {peek.lines.length}</b>
              <button type="button" className="chat-members-x" onClick={() => setPeek(null)} title="Đóng">×</button>
            </div>
            <div className="chat-peek-list">
              {peek.lines.map((l, i) => (
                <div key={i} className="chat-peek-row">
                  <span className="chat-peek-icon">{l.icon}</span>
                  <span className="chat-peek-name">{l.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="chathead" onClick={() => minimized && toggleMinimize(false)}>
        <button className="chathead-btn chathead-back" onClick={(e) => { e.stopPropagation(); onBack ? onBack() : closeWindow(conv.id); }} title="Quay lại danh sách">‹</button>
        <span className="chat-avatar sm chathead-ava cust">{initials(conv.name)}</span>
        <div
          className="chathead-title"
          onClick={onTitleClick ? (e) => { e.stopPropagation(); onTitleClick(); } : undefined}
          style={onTitleClick ? { cursor: 'pointer' } : undefined}
          title={onTitleClick ? 'Bấm để mở/đóng bảng thông tin' : undefined}
        >
          {conv.starred && <span className="chathead-star" title="KH doanh thu cao (được đánh dấu sao)">⭐ </span>}
          {conv.name}
          {minimized && minUnread > 0 && <span className="chathead-unread" style={{ marginLeft: 8 }}>{minUnread > 99 ? '99+' : minUnread}</span>}
          {!minimized && (
            <span className="small" style={{ opacity: .85, fontWeight: 500 }}>
              {conv.custCode ? ` · ${conv.custCode}` : ''}
              {conv.custSaleName && String(conv.custSaleUser || '') !== myId ? ` · Sale: ${conv.custSaleName}` : ''}
            </span>
          )}
        </div>
        {!minimized && (
          <button className="chathead-btn chathead-tasks" onClick={(e) => { e.stopPropagation(); setShowTasks((v) => !v); }} title="Công việc (chỉ nhân viên thấy)">
            📋{openTaskCount(custTasks) > 0 && <span className="chathead-badge">{openTaskCount(custTasks)}</span>}
          </button>
        )}
        {!minimized && canMuteKh(me) && (
          <button className="chathead-btn" onClick={(e) => { e.stopPropagation(); doMute(); }}
            title={muted ? 'Đang tắt âm — bấm để bật lại thông báo' : 'Tắt âm thông báo hội thoại này'}>
            {muted ? '🔕' : '🔔'}
          </button>
        )}
        <button className="chathead-btn chathead-min" onClick={(e) => { e.stopPropagation(); toggleMinimize(); }} title={minimized ? 'Mở lại' : 'Thu nhỏ'}>
          {minimized ? '▢' : '—'}
        </button>
        <button className="chathead-btn" onClick={(e) => { e.stopPropagation(); if (isMobile()) setOpen(false); else closeWindow(conv.id); }} title="Đóng cửa sổ chat">
          ×
        </button>
      </div>
      {showTasks && !minimized && (
        <div className="chat-members chat-taskspanel">
          <div className="chat-members-head">
            <b>📋 Công việc{openTaskCount(custTasks) ? ` (${openTaskCount(custTasks)})` : ''} <span className="small muted">· chỉ nhân viên thấy</span></b>
            <button className="chat-members-x" onClick={() => setShowTasks(false)} title="Đóng">×</button>
          </div>
          <div className="chat-taskspanel-body">
            <ChatTasksPanel tasks={custTasks} assigneeOptions={taskStaff} me={me}
              onCreate={createTaskFromMsg} onAccept={taskAccept} onComplete={taskComplete} onDelete={taskDelete} />
          </div>
        </div>
      )}
      {/* Nhãn/tab KÊNH của khách: >1 kênh -> bấm để chuyển; 1 kênh -> VẪN hiện để biết KH nhắn vào kênh nào */}
      {!minimized && chans.length >= 1 && (
        <div className="cust-chantabs">
          {chans.map((ch) => (
            <button
              key={ch.id}
              type="button"
              className={'cust-chantab' + (String(ch.id) === String(conv.id) ? ' on' : '')}
              onClick={() => String(ch.id) !== String(conv.id) && switchChannel(ch)}
            >
              {SVC_LABEL[ch.svcType] || 'Kinh doanh'}
              {ch.unread > 0 && <span className="cust-chantab-badge">{ch.unread > 9 ? '9+' : ch.unread}</span>}
            </button>
          ))}
        </div>
      )}
      <div className="chatbody">
        <div
          className={'chatthread' + (dragOver ? ' dragover' : '')}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
          onDrop={onDrop}
        >
          {dragOver && <div className="chat-droplay">Thả tệp để gửi</div>}
          <div
            className="chatmsgs"
            ref={msgsRef}
            onScroll={(e) => { const near = isNearBottom(); setScrolledUp(!near); stickBottomRef.current = near; if (newBelow && near) setNewBelow(0); /* cuộn lên gần đỉnh (~1 màn hình) -> tự tải tin cũ SỚM, cuộn vô tận mượt */ if (e.currentTarget.scrollTop < 600 && hasOlder && !loadingOlder) loadOlder(); }}
            // Phóng to phải lấy ẢNH GỐC ở data-full, KHÔNG phải e.target.src — src bây giờ là
            // bản thu nhỏ 480px, dùng nó thì bấm xem to lại ra ảnh mờ. Tệp cũ không có data-full
            // (hoặc ảnh đã lùi về gốc do thumb hỏng) thì src chính là ảnh gốc.
            onClick={(e) => { if (e.target.classList.contains('chat-att-img')) setImgView({ src: e.target.dataset.full || e.target.src, name: e.target.alt }); }}
          >
            {!loadingMsgs && hasOlder && (
              <button type="button" className="chat-loadolder" disabled={loadingOlder} onClick={() => loadOlder(true)}>
                {loadingOlder ? 'Đang tải…' : '↑ Xem tin cũ hơn'}
              </button>
            )}
            {loadingMsgs ? <div className="chatempty">Đang tải…</div> : messageEls}
            {typingNames.length > 0 && (
              <div className="chat-typing">
                <span className="chat-typing-dots"><i /><i /><i /></span>
                <span className="small muted">{typingNames.join(', ')} đang gõ…</span>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {(newBelow > 0 || scrolledUp) && (
            <button type="button" className="chat-jump-latest" onClick={jumpToLatest}>
              {newBelow > 0 ? `↓ ${newBelow} tin nhắn mới` : '↓ Tin mới nhất'}
            </button>
          )}

          {atts.length > 0 && (
            <div className="chat-att-strip">
              {atts.map((a, i) => (
                <div className="chat-att-chip" key={i}>
                  {a.preview ? <img src={a.preview} alt="" /> : <span className="chat-att-doc">📎 {a.name}</span>}
                  <button type="button" className="chat-att-x" onClick={() => setAtts((prev) => prev.filter((_, j) => j !== i))}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {replyTo && (
            <div className="chat-reply-bar">
              <div className="chat-reply-info">
                <span className="chat-reply-label">↩ Trả lời {replyTo.senderName}</span>
                <span className="chat-reply-text">{replyTo.text || 'đã gửi tệp đính kèm'}</span>
              </div>
              <button type="button" className="chat-reply-x" onClick={() => setReplyTo(null)} title="Huỷ trả lời">×</button>
            </div>
          )}

          {imgView && <ImageLightbox src={imgView.src} filename={imgView.name} onClose={() => setImgView(null)} />}

          <form className="chatcompose" onSubmit={submit}>
            {showEmoji && <EmojiPicker onPick={insertEmoji} onSend={sendSticker} />}
            {pastePrompt && (
              <div className="paste-choice">
                <div className="paste-choice-msg">Nội dung vừa dán có <b>cả ảnh và văn bản</b>. Bạn muốn gửi dạng nào?</div>
                <div className="paste-choice-btns">
                  <button type="button" className="btn sm" onClick={choosePasteImage}>🖼 Gửi ảnh</button>
                  <button type="button" className="btn sm ghost" onClick={choosePasteText}>📝 Gửi văn bản</button>
                  <button type="button" className="paste-choice-x" title="Bỏ qua" onClick={() => setPastePrompt(null)}>×</button>
                </div>
              </div>
            )}
            <button
              ref={emojiBtnRef}
              type="button"
              className={'chathead-btn chat-attbtn' + (showEmoji ? ' on' : '')}
              title="Sticker & emoji"
              onClick={() => setShowEmoji((v) => !v)}
            >
              😊
            </button>
            <button type="button" className="chathead-btn chat-attbtn" title="Đính kèm tệp" onClick={() => fileRef.current?.click()}>
              📎
            </button>
            <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => addFiles(e.target.files)} />
            <textarea
              ref={inputRef}
              rows={1}
              defaultValue={initialTextRef.current}
              onChange={(e) => { setText(e.target.value); notifyCustTyping(e.target.value.trim().length > 0); }}
              onPaste={onPaste}
              onFocus={() => setComposerFocused(conv.id, true)}
              onBlur={() => setComposerFocused(conv.id, false)}
              onKeyDown={(e) => {
                // MÁY TÍNH: Enter gửi, Shift+Enter xuống dòng. ĐIỆN THOẠI: Enter xuống dòng, gửi bằng nút.
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing && !isMobile()) {
                  e.preventDefault();
                  e.currentTarget.form?.requestSubmit();
                }
              }}
              placeholder="Nhắn cho khách…"
              autoFocus={front && !isMobile()}
            />
            <button type="submit" className="btn sm" disabled={sending || (!text.trim() && !atts.length)}>
              {sending ? '…' : 'Gửi'}
            </button>
          </form>
        </div>
      </div>
      {taskMsg && (
        <ChatTaskModal
          msg={taskMsg}
          assigneeOptions={taskStaff}
          me={me}
          onCreate={createTaskFromMsg}
          onClose={() => setTaskMsg(null)}
        />
      )}
    </div>
  );
}
